/**
 * Rolling retention for bell-tray notifications (4 weeks).
 * Deletes party-owned docs older than the cutoff so they leave the dropdown and stop inflating unread.
 */
import {
  collection,
  getDocs,
  limit,
  query,
  where,
  writeBatch,
  type Firestore,
} from 'firebase/firestore';
import {
  getNotificationTrayRetentionCutoff,
  NOTIFICATION_TRAY_RETENTION_DAYS,
} from '../constants/notificationTrayUi';
import { DEFAULT_LIST_LIMIT } from '../constants/queryLimits';
import Logger from './logger';

const MAX_DELETE_PER_RUN = 50;

function createdAtFromDoc(data: { createdAt?: { toDate?: () => Date } | Date }): Date | null {
  if (data.createdAt && typeof (data.createdAt as { toDate?: () => Date }).toDate === 'function') {
    return (data.createdAt as { toDate: () => Date }).toDate();
  }
  if (data.createdAt instanceof Date) return data.createdAt;
  return null;
}

/**
 * @param partyFields Equality fields identifying this user's notifications
 *   (e.g. carrier: recipientId; broker: brokerId, userId; shipper: shipperId, userId).
 */
export async function cleanupExpiredBellTrayNotifications(
  db: Firestore,
  userId: string,
  partyFields: readonly string[]
): Promise<number> {
  if (!userId || partyFields.length === 0) return 0;

  const cutoffMs = getNotificationTrayRetentionCutoff().getTime();
  let deleted = 0;

  for (const field of partyFields) {
    if (deleted >= MAX_DELETE_PER_RUN) break;
    try {
      const snap = await getDocs(
        query(collection(db, 'notifications'), where(field, '==', userId), limit(DEFAULT_LIST_LIMIT))
      );
      const expired = snap.docs.filter((d) => {
        const createdAt = createdAtFromDoc(d.data());
        return createdAt != null && createdAt.getTime() < cutoffMs;
      });
      if (expired.length === 0) continue;

      const batch = writeBatch(db);
      const slice = expired.slice(0, MAX_DELETE_PER_RUN - deleted);
      slice.forEach((d) => batch.delete(d.ref));
      await batch.commit();
      deleted += slice.length;
    } catch (err) {
      Logger.warn('[cleanupExpiredBellTrayNotifications] Failed for field', {
        err,
        field,
        userId,
      });
    }
  }

  if (deleted > 0) {
    Logger.debug(
      `Removed ${deleted} notification(s) older than ${NOTIFICATION_TRAY_RETENTION_DAYS} days for ${userId}`
    );
  }
  return deleted;
}
